import React from "react";
import { Box, Container, Typography, Button, keyframes } from "@mui/material";
import { useTheme } from "@emotion/react";
import { Link as RouterLink } from "react-router-dom";
import { getPageRoute } from "src/services";
import { ReactComponent as UsOutlineMap } from "src/assets/us-outline.svg";
import WaitlistCTA from "./WaitlistCTA.js";

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

// Ring pulse for city markers. Sized for the marker overlay SVG's viewBox,
// which matches the imported US outline (0 0 2000 1200) - hence the larger
// radii than the previous hand-drawn map.
const pulseRing = keyframes`
  0% { r: 18; opacity: 0.8; }
  100% { r: 72; opacity: 0; }
`;

const cities = [
  {
    key: "houston",
    status: "live",
    name: "Houston, TX",
    blurb:
      "Year-one launch. Charter network partner. Every classroom beat national growth norms in math and science.",
  },
  {
    key: "sf",
    status: "live",
    name: "San Francisco, CA",
    blurb: "Public + charter pilots. Year-one classrooms live in both.",
  },
  {
    key: "roadmap",
    status: "roadmap",
    name: "New York + Los Angeles",
    blurb:
      "Two of the largest K-12 STEM populations in the country. Actively partnering with districts for fall 2026.",
  },
];

const WhereWereLive = () => {
  const theme = useTheme();

  return (
    <Box
      component="section"
      id="live"
      sx={{
        py: { xs: 8, md: 14 },
        bgcolor: "#0f172a",
        color: theme.palette.paper.primary,
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        <Typography
          sx={{
            display: "inline-block",
            fontSize: 12,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: theme.palette.success.main,
            fontWeight: 700,
            mb: 2.5,
          }}
        >
          Where we're live
        </Typography>
        <Typography
          component="h2"
          sx={{
            fontSize: { xs: "2rem", md: "3.25rem" },
            fontWeight: 800,
            letterSpacing: "-0.03em",
            lineHeight: 1.06,
            color: theme.palette.paper.primary,
          }}
        >
          Live in two cities.{" "}
          <Box component="span" sx={{ color: theme.palette.success.main }}>
            Moving into two more
          </Box>
          .
        </Typography>
        <Typography
          sx={{
            fontSize: "1.25rem",
            color: "rgba(255,255,255,0.7)",
            mt: 2.75,
            maxWidth: 640,
            lineHeight: 1.5,
          }}
        >
          Maestro launched in Houston and San Francisco in late 2025. We're
          moving into NY and LA on the strength of the year-one data.
        </Typography>

        <Box
          sx={{
            mt: 8,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.4fr 1fr" },
            gap: 7.5,
            alignItems: "center",
          }}
        >
          {/* Map - real contiguous-US SVG outline (src/assets/us-outline.svg),
              with city markers overlaid in a matching-viewBox SVG so the two
              layers stay aligned as the container resizes. */}
          <Box sx={{ position: "relative", width: "100%" }}>
            <Box
              component={UsOutlineMap}
              sx={{
                width: "100%",
                height: "auto",
                display: "block",
                color: "rgba(255,255,255,0.35)",
              }}
            />
            <Box
              component="svg"
              viewBox="0 0 2000 1200"
              xmlns="http://www.w3.org/2000/svg"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
              }}
            >
              {/* SF (live) - coastal California */}
              <g transform="translate(255, 555)">
                <Box
                  component="circle"
                  cx="0"
                  cy="0"
                  r="18"
                  sx={{
                    fill: "none",
                    stroke: theme.palette.success.main,
                    strokeWidth: 4,
                    opacity: 0.6,
                    animation: `${pulseRing} 2.4s ease-out infinite`,
                  }}
                />
                <circle
                  cx="0"
                  cy="0"
                  r="18"
                  style={{ fill: theme.palette.success.main }}
                />
                <text
                  x="32"
                  y="-8"
                  style={{
                    fill: "#fff",
                    fontSize: 30,
                    fontWeight: 700,
                  }}
                >
                  San Francisco
                </text>
                <text
                  x="32"
                  y="26"
                  style={{
                    fill: "rgba(255,255,255,0.6)",
                    fontSize: 24,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                  }}
                >
                  Live · 2025
                </text>
              </g>

              {/* Houston (live) - southeast Texas */}
              <g transform="translate(950, 890)">
                <Box
                  component="circle"
                  cx="0"
                  cy="0"
                  r="18"
                  sx={{
                    fill: "none",
                    stroke: theme.palette.success.main,
                    strokeWidth: 4,
                    opacity: 0.6,
                    animation: `${pulseRing} 2.4s ease-out infinite`,
                  }}
                />
                <circle
                  cx="0"
                  cy="0"
                  r="18"
                  style={{ fill: theme.palette.success.main }}
                />
                <text
                  x="32"
                  y="-8"
                  style={{ fill: "#fff", fontSize: 30, fontWeight: 700 }}
                >
                  Houston
                </text>
                <text
                  x="32"
                  y="26"
                  style={{
                    fill: "rgba(255,255,255,0.6)",
                    fontSize: 24,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                  }}
                >
                  Live · 2025
                </text>
              </g>

              {/* NY (roadmap) - label anchored to LEFT so it doesn't clip past the viewBox at narrow widths */}
              <g transform="translate(1800, 400)">
                <circle
                  cx="0"
                  cy="0"
                  r="15"
                  style={{
                    fill: "rgba(255,255,255,0.2)",
                    stroke: "rgba(255,255,255,0.5)",
                    strokeWidth: 3,
                  }}
                />
                <text
                  x="-32"
                  y="-8"
                  textAnchor="end"
                  style={{
                    fill: "#fff",
                    fontSize: 30,
                    fontWeight: 700,
                    opacity: 0.7,
                  }}
                >
                  New York
                </text>
                <text
                  x="-32"
                  y="26"
                  textAnchor="end"
                  style={{
                    fill: "rgba(255,255,255,0.6)",
                    fontSize: 24,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                  }}
                >
                  On the roadmap
                </text>
              </g>

              {/* LA (roadmap) - southern California */}
              <g transform="translate(345, 720)">
                <circle
                  cx="0"
                  cy="0"
                  r="15"
                  style={{
                    fill: "rgba(255,255,255,0.2)",
                    stroke: "rgba(255,255,255,0.5)",
                    strokeWidth: 3,
                  }}
                />
                <text
                  x="32"
                  y="-8"
                  style={{
                    fill: "#fff",
                    fontSize: 30,
                    fontWeight: 700,
                    opacity: 0.7,
                  }}
                >
                  Los Angeles
                </text>
                <text
                  x="32"
                  y="26"
                  style={{
                    fill: "rgba(255,255,255,0.6)",
                    fontSize: 24,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                  }}
                >
                  On the roadmap
                </text>
              </g>
            </Box>
          </Box>

          {/* City list */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {cities.map((c) => (
              <Box
                key={c.key}
                sx={{
                  p: 3,
                  bgcolor:
                    c.status === "live"
                      ? "rgba(32,156,51,0.08)"
                      : "rgba(255,255,255,0.05)",
                  border:
                    c.status === "live"
                      ? `1px solid ${theme.palette.success.main}`
                      : "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    fontSize: 11,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    mb: 1.25,
                    color:
                      c.status === "live"
                        ? theme.palette.success.main
                        : "rgba(255,255,255,0.5)",
                  }}
                >
                  {c.status === "live" && (
                    <Box
                      component="span"
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: theme.palette.success.main,
                        animation: `${pulse} 2s infinite`,
                      }}
                    />
                  )}
                  {c.status === "live" ? "Live" : "On the roadmap"}
                </Box>
                <Typography
                  sx={{
                    fontSize: "1.25rem",
                    fontWeight: 700,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {c.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    color: "rgba(255,255,255,0.65)",
                    mt: 0.75,
                  }}
                >
                  {c.blurb}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Report CTA */}
        <Box
          sx={{
            mt: 7,
            p: { xs: 4, md: 5 },
            background:
              "linear-gradient(135deg, rgba(32,156,51,0.15) 0%, rgba(32,156,51,0.03) 100%)",
            border: `1px solid ${theme.palette.success.main}`,
            borderRadius: 3,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
            gap: 3,
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 11,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: theme.palette.success.main,
                fontWeight: 700,
              }}
            >
              Year-one report
            </Typography>
            <Typography
              sx={{
                fontSize: "1.5rem",
                fontWeight: 700,
                color: theme.palette.paper.primary,
                mt: 1,
                lineHeight: { xs: 1.2 },
                letterSpacing: "-0.01em",
              }}
            >
              Every classroom. Every cohort. The methodology.
            </Typography>
            <Typography
              sx={{
                fontSize: "0.875rem",
                color: "rgba(255,255,255,0.65)",
                mt: 0.75,
              }}
            >
              Detailed breakdown of the 28-classroom pilot, cohort-level
              Conditional Growth Percentiles, and what changed in each teacher's
              workflow.
            </Typography>
          </Box>
          <Button
            variant="contained"
            component={RouterLink}
            to={getPageRoute("home", "REPORT")}
            sx={{
              bgcolor: theme.palette.success.main,
              color: theme.palette.paper.primary,
              px: 3,
              py: 1.75,
              fontSize: "0.875rem",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: 1.25,
              whiteSpace: "nowrap",
              "&:hover": { bgcolor: "#15803d" },
            }}
          >
            Get the report →
          </Button>
        </Box>

        <WaitlistCTA variant="dark" />
      </Container>
    </Box>
  );
};

export default WhereWereLive;
